import { useEffect, useMemo, useState } from "react";
import Image, { type ImageProps, type ImageLoader } from "next/image";

type ForwardedImageProps = Pick<
  ImageProps,
  | "alt"
  | "src"
  | "width"
  | "height"
  | "fill"
  | "sizes"
  | "quality"
  | "priority"
  | "loading"
  | "unoptimized"
  | "onLoadingComplete"
  | "onError"
  | "className"
  | "style"
  | "crossOrigin"
  | "referrerPolicy"
  | "fetchPriority"
  | "decoding"
>;

type BaseOverrides = {
  sbFormat?: "webp" | "original";
  sbQuality?: number;
  disableAutoBlur?: boolean;
  blurWidth?: number;
  blurAmount?: number;
  src: string;
};

export type NextLazyImageFutureProps = Omit<ForwardedImageProps, "placeholder" | "blurDataURL" | "loader"> &
  BaseOverrides;

const STORYBLOK_HOSTS = new Set(["a.storyblok.com", "img2.storyblok.com"]);
const DEFAULT_QUALITY = 70;
const DEFAULT_FORMAT: "webp" | "original" = "webp";
const DEFAULT_BLUR_WIDTH = 24;
const DEFAULT_BLUR_AMOUNT = 8;

export default function NextLazyImageFuture(props: NextLazyImageFutureProps) {
  const {
    src,
    alt,
    style,
    className,
    sizes,
    priority,
    sbFormat = DEFAULT_FORMAT,
    sbQuality = DEFAULT_QUALITY,
    disableAutoBlur,
    blurWidth = DEFAULT_BLUR_WIDTH,
    blurAmount = DEFAULT_BLUR_AMOUNT,
    ...rest
  } = props;

  const normalizedSrc = useMemo(() => normalizeUrl(src), [src]);
  const isStoryblok = useMemo(() => looksLikeStoryblok(normalizedSrc), [normalizedSrc]);
  const isTransformable = useMemo(() => isStoryblok && isRasterImage(normalizedSrc), [isStoryblok, normalizedSrc]);

  const loader: ImageLoader | undefined = useMemo(
    () => (isTransformable ? storyblokLoaderFactory({ format: sbFormat, defaultQuality: sbQuality }) : undefined),
    [isTransformable, sbFormat, sbQuality],
  );

  const [blurDataURL, setBlurDataURL] = useState<string | undefined>();
  const [placeholder, setPlaceholder] = useState<ImageProps["placeholder"] | undefined>();

  useEffect(() => {
    let cancelled = false;
    if (disableAutoBlur || !isTransformable) return;
    const params = new URLSearchParams({
      src: normalizedSrc,
      w: String(blurWidth),
      blur: String(blurAmount),
      format: sbFormat,
      q: "30",
    });
    fetch(`/api/sb-lqip?${params.toString()}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data?.dataUrl) {
          setBlurDataURL(data.dataUrl);
          setPlaceholder("blur");
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [disableAutoBlur, isTransformable, normalizedSrc, blurWidth, blurAmount, sbFormat]);

  const resolvedSizes = "fill" in rest && rest.fill && !sizes ? "100vw" : sizes;

  return (
    <Image
      alt={alt}
      src={normalizedSrc}
      loader={loader}
      placeholder={placeholder}
      blurDataURL={blurDataURL}
      sizes={resolvedSizes}
      priority={priority}
      className={className}
      style={{ objectFit: "cover", ...style }}
      {...rest}
    />
  );
}

function normalizeUrl(input: string) {
  if (input.startsWith("//")) return `https:${input}`;
  return input;
}
function looksLikeStoryblok(u: string) {
  try {
    const url = new URL(u);
    return STORYBLOK_HOSTS.has(url.hostname);
  } catch {
    return false;
  }
}
function isRasterImage(u: string) {
  const lower = u.toLowerCase();
  return !/\.(svg|pdf|ico)(\?|#|$)/i.test(lower);
}
function storyblokLoaderFactory(opts: { format: "webp" | "original"; defaultQuality: number }): ImageLoader {
  const { format, defaultQuality } = opts;
  return ({ src, width, quality }) => {
    const base = src.replace(/\/m\/.*$/i, "");
    const q = quality ?? defaultQuality;
    const filters = [`quality(${q})`];
    if (format === "webp") filters.push("format(webp)");
    return `${base}/m/${width}x0/filters:${filters.join(":")}`;
  };
}
